import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SignaturePad } from "@/components/SignaturePad";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const RESULTS = ["Pass", "Fail", "N/A"];

export function ChecklistFill({ installationId, formId, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [header, setHeader] = useState({ contractor: "", agency: "", date: "" });
  const [responses, setResponses] = useState({});
  const [deficiencies, setDeficiencies] = useState("");
  const [signatures, setSignatures] = useState({}); // role -> { name, image_data_url }

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/installations/${installationId}/checklist/${formId}`).then((r) => {
      const d = r.data;
      setData(d);
      const sub = d.submission;
      const items = d.form.items || [];
      const resp = {};
      items.forEach((it) => {
        const existing = sub?.responses?.find((x) => x.item_id === it.id);
        resp[it.id] = { result: existing?.result || "", comment: existing?.comment || "" };
      });
      setResponses(resp);
      setHeader({ contractor: sub?.header?.contractor || "", agency: sub?.header?.agency || "", date: sub?.header?.date || new Date().toISOString().slice(0, 10) });
      setDeficiencies(sub?.deficiencies || "");
      const sig = {};
      (d.form.signature_roles || []).forEach((role) => {
        const ex = sub?.signatures?.find((s) => s.role === role);
        sig[role] = { name: ex?.name || "", image_data_url: ex?.image_data_url || null };
      });
      setSignatures(sig);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open, installationId, formId]);

  const setResp = (itemId, field, val) => setResponses((s) => ({ ...s, [itemId]: { ...s[itemId], [field]: val } }));
  const setSig = (role, field, val) => setSignatures((s) => ({ ...s, [role]: { ...s[role], [field]: val } }));

  const save = async () => {
    setBusy(true);
    try {
      const items = data.form.items || [];
      const payload = {
        header,
        responses: items.map((it) => ({ item_id: it.id, text: it.text, result: responses[it.id]?.result || null, comment: responses[it.id]?.comment || null })),
        deficiencies,
        signatures: (data.form.signature_roles || []).map((role) => ({ role, name: signatures[role]?.name || null, image_data_url: signatures[role]?.image_data_url || null })).filter((s) => s.name || s.image_data_url),
        status: "completed",
      };
      await api.post(`/installations/${installationId}/checklist/${formId}`, payload);
      toast.success(t("checklist.saved"));
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-slate-200 bg-white" data-testid="checklist-fill">
        {!data ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-slate-900">{data.form.name}</DialogTitle>
              <DialogDescription>{data.project_name} · {data.bus_number || data.vin}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div><Label className="overline text-slate-500">{t("checklist.contractor")}</Label><Input data-testid="chk-contractor" value={header.contractor} onChange={(e) => setHeader({ ...header, contractor: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("checklist.agency")}</Label><Input data-testid="chk-agency" value={header.agency} onChange={(e) => setHeader({ ...header, agency: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
              <div><Label className="overline text-slate-500">{t("checklist.date")}</Label><Input data-testid="chk-date" type="date" value={header.date} onChange={(e) => setHeader({ ...header, date: e.target.value })} className="mt-1.5 border-slate-300 bg-white" /></div>
            </div>

            <div className="mt-2 space-y-2">
              <div className="overline text-slate-500">{t("checklist.items")}</div>
              {(data.form.items || []).map((it, i) => (
                <div key={it.id} className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={`chk-item-${it.id}`}>
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-5 text-right text-xs text-slate-400">{i + 1}.</span>
                    <div className="flex-1">
                      <div className="text-sm text-slate-800">{it.text}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {RESULTS.map((res) => (
                          <button key={res} type="button" data-testid={`chk-${it.id}-${res}`} onClick={() => setResp(it.id, "result", res)}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${responses[it.id]?.result === res ? (res === "Pass" ? "bg-emerald-600 text-white" : res === "Fail" ? "bg-red-600 text-white" : "bg-slate-500 text-white") : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-100"}`}>
                            {res}
                          </button>
                        ))}
                        <Input data-testid={`chk-${it.id}-comment`} value={responses[it.id]?.comment || ""} onChange={(e) => setResp(it.id, "comment", e.target.value)} placeholder={t("checklist.commentPlaceholder")} className="h-8 flex-1 border-slate-300 bg-white text-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2">
              <Label className="overline text-slate-500">{t("checklist.deficiencies")}</Label>
              <Textarea data-testid="chk-deficiencies" value={deficiencies} onChange={(e) => setDeficiencies(e.target.value)} className="mt-1.5 border-slate-300 bg-white" />
            </div>

            <div className="mt-2 space-y-3">
              <div className="overline text-slate-500">{t("checklist.signoff")}</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {(data.form.signature_roles || []).map((role) => (
                  <div key={role} className="rounded-md border border-slate-200 p-3" data-testid={`chk-sig-${role}`}>
                    <div className="mb-1.5 text-sm font-medium text-slate-800">{role}</div>
                    <Input data-testid={`chk-sig-name-${role}`} value={signatures[role]?.name || ""} onChange={(e) => setSig(role, "name", e.target.value)} placeholder={t("checklist.printedName")} className="mb-2 h-8 border-slate-300 bg-white text-sm" />
                    <SignaturePad dataUrl={signatures[role]?.image_data_url} onChange={(url) => setSig(role, "image_data_url", url)} />
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="border-slate-300">{t("checklist.cancel")}</Button>
              <Button data-testid="save-checklist" onClick={save} disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-500">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("checklist.saveSign")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
